// @soft-depth
import React from 'react';
import moment from 'moment';
import Button from '@mui/material/Button';
import { Pencil } from 'lucide-react';
import { usePickerContext } from '@mui/x-date-pickers/hooks';
import { DatePicker as ClickableDatePicker } from '../../ClickableField/ClickableField';
import SoftDepthScope from '../../../design-system/SoftDepthScope';
import LegacyThemeScope from '../../../design-system/LegacyThemeScope';

/**
 * The 「先設定生日」 link that opens a date picker in place, instead of sending
 * the adviser off to the factsheet form. Shared by Smart IDEA and ClientV2's
 * 招募 IDEA; each host saves the date its own way through `onPick`.
 *
 * The link is Soft Depth (business mode, in 招募 too); the date picker itself stays
 * on the legacy theme it was built against.
 */

/** 「先設定生日 ✎」 as an accent text button. DobReadings uses it without a picker. */
export const SetBirthdayLink = React.forwardRef<HTMLButtonElement, { label: string; onClick?: () => void }>(
  ({ label, onClick }, ref) => (
    <SoftDepthScope mode="business">
      <Button
        ref={ref}
        variant="text"
        size="small"
        onClick={onClick}
        endIcon={<Pencil size={14} aria-hidden="true" />}
        sx={{ m: -0.5, px: 0.5, py: 0, minWidth: 0, color: 'primary.dark' }}
      >
        {label}
      </Button>
    </SoftDepthScope>
  )
);
SetBirthdayLink.displayName = 'SetBirthdayLink';

// Picker field slot that looks like the link: tapping it opens the date
// picker right here, anchored to the link on desktop.
const SetDobHintField = ({ hintText }: { hintText: string }) => {
  const { setOpen, triggerRef } = usePickerContext();
  return (
    <SetBirthdayLink ref={triggerRef as React.Ref<HTMLButtonElement>} label={hintText} onClick={() => setOpen(true)} />
  );
};

export interface SetBirthdayPickerProps {
  hintText: string;
  /** Receives the accepted date as `YYYY-MM-DD`. */
  onPick: (value: string) => void;
}

const SetBirthdayPicker = ({ hintText, onPick }: SetBirthdayPickerProps) => (
  <LegacyThemeScope>
    <ClickableDatePicker
      name="dob"
      format="YYYY-MM-DD"
      openTo="year"
      referenceDate={moment().subtract(30, 'years')}
      maxDate={moment()}
      onAccept={(date: moment.Moment | null) => {
        if (date && date.isValid()) onPick(date.format('YYYY-MM-DD'));
      }}
      slots={{ field: SetDobHintField }}
      slotProps={{
        // Both hosts sit inside BottomSheetOrDialog, which already owns the body
        // scroll lock — same reason as ClientDetailDrawer's dob picker.
        dialog: { disableScrollLock: true },
        field: { hintText }
      }}
    />
  </LegacyThemeScope>
);

export default SetBirthdayPicker;
